// conponents/BlogAdminPanel.js

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from '../services/axios';
import { useSpinner } from '../context/SpinnerContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useThemeContext } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useBlog } from '../context/BlogContext';
import { Editor } from '@tinymce/tinymce-react';
const PageWrapper = styled.div`
  padding: 40px 20px;
  background: ${({ theme }) => theme.background || '#f9f9f9'};
  min-height: 100vh;
  font-family: ${({ theme }) => theme.font || 'sans-serif'};
`;

const PanelContainer = styled.div`
  padding: 2rem;
  background: ${({ theme }) => theme.card || '#fff'};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-width: 1000px;
  margin: 0 auto;
`;

const Section = styled.section`
  margin-bottom: 2rem;
  padding: 1.25rem;
  background: ${({ theme }) => theme.section || '#f0f0f0'};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border || '#ccc'};
`;

const SubHeading = styled.h3`
  color: ${({ theme }) => theme.text || '#333'};
  margin-bottom: 1rem;
  font-size: 1.1rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px;
  border-bottom: 2px solid ${({ theme }) => theme.border || '#ccc'};
`;

const Td = styled.td`
  padding: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.borderLight || '#ddd'};
  text-align: center;
`;

const InputRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 1rem;
  align-items: center;
`;

const Input = styled.input`
  flex: 2;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.inputBorder || '#ccc'};
`;

const RoleSelect = styled.select`
  padding: 8px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.inputBorder || '#ccc'};
`;

const Button = styled.button`
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.primary || '#007bff'};
  color: ${({ theme }) => theme.buttonText || 'white'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  min-width: 100px;

  &:hover {
    background-color: ${({ theme }) => theme.primaryHover || '#0069d9'};
  }
`;

const ErrorText = styled.p`
  color: #b00020;
  font-size: 0.9rem;
  margin-top: 1rem;
`;

const SuccessText = styled.p`
  color: green;
  font-size: 0.9rem;
  margin-top: 1rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.text || '#333'};
`;

const InviteContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.border || '#ccc'};
  border-radius: 10px;
  padding: 1rem;
  background: ${({ theme }) => theme.section || '#f9f9f9'};
  margin-top: 1rem;
  position: relative;
`;

const TabWrapper = styled.div`
  display: flex;
  position: absolute;
  top: -1.5rem;
  left: 1rem;
`;

const StyledTab = styled.button`
  padding: 8px 16px;
  border: 1px solid ${({ theme }) => theme.border || '#ccc'};
  border-bottom: none;
  background: ${({ active, theme }) => active ? theme.card || '#fff' : '#ddd'};
  color: ${({ active }) => active ? '#000' : '#555'};
  font-weight: 600;
  border-radius: 10px 10px 0 0;
  margin-right: 5px;
  cursor: pointer;
  min-width: 140px;
  transition: background 0.3s;

  &:hover {
    background: ${({ active }) => active ? '#fff' : '#ccc'};
  }
`;

const InviteHeader = styled.h3`
  margin-bottom: 2.5rem;
  font-size: 1.2rem;
  font-weight: bold;
  color: ${({ theme }) => theme.text || '#333'};
`;

const ThemeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 1rem;
`;

const ThemeCard = styled.div`
  border: ${({ selected, theme }) => selected ? `3px solid ${theme.primary}` : `1px solid ${theme.border}`};
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  box-shadow: ${({ selected }) =>
    selected ? '0 4px 20px rgba(0, 123, 255, 0.4)' : '0 2px 6px rgba(0, 0, 0, 0.05)'};
  transform: ${({ selected }) => (selected ? 'scale(1.03)' : 'scale(1)')};
  transition: all 0.2s ease-in-out;
  background: ${({ theme }) => theme.card};
  text-align: center;
`;

const ThemePreview = styled.div.attrs(({ $themeItem }) => ({
  style: {
    backgroundColor: $themeItem?.background_color || '#ffffff',
    backgroundImage: $themeItem?.background_image_url
      ? `url(${$themeItem.background_image_url})`
      : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
}))`
  width: 100%;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #ddd;
`;

const ThemeColors = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const ColorSwatch = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ color }) => color};
  border: 1px solid #aaa;
`;

const FontSample = styled.div`
  font-family: ${({ $font }) => $font};
  font-size: 0.9rem;
  color: #333;
`;


const ROLE_OPTIONS = [
  { label: 'Viewer', value: 'viewer' },
  { label: 'Author', value: 'author' },
  { label: 'Admin', value: 'admin' },
  { label: 'Owner', value: 'owner' }
];

const DEFAULT_BLOG_DESCRIPTION =
  'Welcome to your blog! Customize your description in the Admin Dashboard.';


const BlogAdminPanel = ({ blog }) => {
  const { theme, applyTheme } = useThemeContext();
  const { setLoading } = useSpinner();
  const { selectBlog } = useBlog();
  
  const [blogDetails, setBlogDetails] = useState(blog);
  const [members, setMembers] = useState([]);
  const [usernameToInvite, setUsernameToInvite] = useState('');
  const [newUserRole, setNewUserRole] = useState('viewer');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('existing');
  const [themes, setThemes] = useState([]);
  const [selectedThemeId, setSelectedThemeId] = useState(blog.theme_id || null);
  const [allUsers, setAllUsers]  = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const { logout, refreshBlogAuthz } = useAuth();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [headerFile, setHeaderFile] = useState(null);
  const [savingHeader, setSavingHeader] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const navigate = useNavigate();

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/admin/blogs/${blog.id}/members/`);
      setMembers(res.data.members);
      setBlogDetails(res.data.blog);
      setError('');
    } catch (err) {
      setError('Failed to load blog members.');
    } finally {
      setLoading(false);
    }
  };

  const fetchThemes = async () => {
    const res = await axios.get('/api/themes/'); // or your actual endpoint
    return res.data;
  };

  const [showDescPreview, setShowDescPreview] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get('/api/admin/list_all_users/'); 
        setAllUsers(res.data);
      } catch (err) {
        console.error('Failed to load users list', err);
      }
    };
    if (activeTab === 'existing') {
      fetchUsers();
    }
  }, [activeTab]);

  useEffect(() => {
    fetchMembers();
  }, [blog.id]);

  useEffect(() => {
    if (blogDetails) {
      setTitle(blogDetails.title || '');
      setDescription(blogDetails.description || '');
    }
  }, [blogDetails]);

  useEffect(() => {
    fetchThemes().then(setThemes).catch(err => console.error("Failed to load themes", err));
  }, []);

  useEffect(() => {
    if (blog.theme_id && themes.length > 0) {
      const current = themes.find(t => String(t.id) === String(blog.theme_id));
      if (current) {
        setThemeFromData(current);
      }
    }
  }, [themes]);

  const handleToggleComments = async () => {
    setLoading(true);
    try {
      const res = await axios.patch(`/api/admin/blogs/${blog.id}/`, {
        allow_comments: !blogDetails.allow_comments,
      });
      setBlogDetails(prev => ({ ...prev, allow_comments: res.data.allow_comments }));
      setSuccess('Comment setting updated.');
    } catch {
      setError('Could not update comment settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleReactions = async () => {
    setLoading(true);
    try {
      const res = await axios.patch(`/api/admin/blogs/${blog.id}/`, {
        allow_reactions: !blogDetails.allow_reactions,
      });
      setBlogDetails(prev => ({ ...prev, allow_reactions: res.data.allow_reactions }));
      setSuccess('Reaction setting updated.');
    } catch {
      setError('Could not update reaction settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotifications = async (field) => {
    setLoading(true);
    try {
      const res = await axios.patch(`/api/admin/blogs/${blog.id}/`, {
        [field]: !blogDetails[field],
      });
      setBlogDetails(prev => ({ ...prev, [field]: res.data[field] }));
      setSuccess('Notification setting updated.');
    } catch {
      setError('Could not update notification settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBlogField = async (field, value) => {
    setLoading(true);
    try {
      const res = await axios.patch(`/api/admin/blogs/${blog.id}/`, {
        [field]: value,
      });
      setBlogDetails(prev => ({ ...prev, [field]: res.data[field] }));
      setSuccess('Settings updated.');
    } catch {
      setError('Could not update settings.');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/metrics/blog/analytics/');
      setAnalytics(res.data);
    } catch (err) {
      setError('Failed to load analytics (check metrics mode = Standard).');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setLoading(true);
    try {
      await axios.patch(`/api/admin/blogs/${blog.id}/members/${userId}/`, {
        role: newRole,
      });

      const isCurrentUser = userId === blogDetails.current_user_id;
      if (isCurrentUser && newRole === 'viewer') {
        setSuccess('You have changed your role to Viewer. Redirecting...');
        setTimeout(() => navigate('/'), 3000);
      } else {
        setSuccess('Role updated.');
      }

      fetchMembers();
    } catch {
      setError('Could not update user role.');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    setLoading(true);
    try {
      if (activeTab === 'existing') {
        const promises = selectedUserIds.map(user_id =>
          axios.post(`/api/admin/blogs/${blog.id}/invite/`, {
            user_id,
            role: newUserRole
          })
        );
        await Promise.all(promises);
        setSelectedUserIds([]);
      } else {
        await axios.post(`/api/admin/blogs/${blog.id}/invite/`, {
          email: usernameToInvite,
          role: newUserRole
        });
        setUsernameToInvite('');
      }

      setSuccess('User(s) invited successfully.');
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to invite user(s).');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHeader = async () => {
    if (!blog?.id) return;

    setSavingHeader(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);

      if (headerFile) {
        formData.append('header_image', headerFile);
      }

      const res = await axios.patch(
        `/api/dashboard/${blog.id}/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setBlogDetails(res.data.blog || res.data);
      setHeaderFile(null);

      const meta = await axios.get(`/api/blogs/${blog.slug}/metadata/`);
      selectBlog({ ...meta.data });
      await refreshBlogAuthz();

      setSuccess('Header settings updated successfully.');
    } catch (err) {
      console.error(err);
      setError('Failed to update header settings.');
    } finally {
      setSavingHeader(false);
    }
  };


  const setThemeFromData = (themeData) => {
    applyTheme(themeData);
  };

  return (
    <>
      <Header
        customButtons={
          <Button theme={theme} onClick={() => navigate('/dashboard')}>
            ← Back to Blog
          </Button>
        }
      />
      <PageWrapper theme={theme}>
        <PanelContainer theme={theme}>

          <Section theme={theme}>
            <SubHeading theme={theme}>Header Settings</SubHeading>

            <InputRow>
              <Input
                theme={theme}
                type="text"
                placeholder="Blog title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </InputRow>


            <div style={{ marginTop: '12px' }}>
              <div style={{ fontWeight: 600, marginBottom: '6px', color: theme.text }}>
                Blog Description
              </div>

              <InputRow>
                <Button
                  theme={theme}
                  type="button"
                  onClick={() => setShowDescPreview((v) => !v)}
                >
                  {showDescPreview ? 'Hide Preview' : 'Show Preview'}
                </Button>

                <Button
                  theme={theme}
                  type="button"
                  onClick={() => {
                    setDescription('');
                    setShowDescPreview(true);
                  }}
                >
                  Reset to Default
                </Button>
              </InputRow>


              <Editor
                value={description}
                onEditorChange={(content) => setDescription(content)}
                init={{
                  height: 260,
                  menubar: false,
                  plugins: 'link lists code',
                  toolbar:
                    'undo redo | bold italic underline | bullist numlist | link | removeformat | code',
                  branding: false,
                }}
              />

              {showDescPreview && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.border || '#ccc'}`,
                    background: theme.card || '#fff',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '6px' }}>
                    Preview
                  </div>

                  <div
                    style={{ lineHeight: 1.5 }}
                    dangerouslySetInnerHTML={{
                      __html: description?.trim()
                        ? description
                        : DEFAULT_BLOG_DESCRIPTION,
                    }}
                  />
                </div>
              )}

            </div>

            {blogDetails?.header_image_url && (
              <div style={{ marginTop: '18px', marginBottom: '14px' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px' }}>Current header</div>

                <img
                  src={blogDetails.header_image_url}
                  alt="Blog header"
                  style={{
                    width: '100%',
                    maxWidth: '240px',   // responsive cap
                    height: 'auto',      // ✅ prevents cropping
                    borderRadius: '10px',
                    border: `1px solid ${theme.border || '#ddd'}`,
                    display: 'block',
                  }}
                />
              </div>
            )}


            <InputRow>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setHeaderFile(e.target.files?.[0] || null)}
              />
              {headerFile && (
                <div style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                  New image selected: <strong>{headerFile.name}</strong>
                </div>
              )}

            </InputRow>

            <InputRow>
              <Button theme={theme} onClick={handleSaveHeader} disabled={savingHeader}>
                {savingHeader ? 'Saving…' : 'Save Header'}
              </Button>
            </InputRow>
          </Section>




          <Section theme={theme}>
            <SubHeading theme={theme}>Settings</SubHeading>
            <CheckboxLabel theme={theme}>
              <input
                type="checkbox"
                checked={blogDetails.allow_reactions}
                onChange={handleToggleReactions}
              />
              Allow Reactions
            </CheckboxLabel>
            <CheckboxLabel theme={theme}>
              <input
                type="checkbox"
                checked={blogDetails.allow_comments}
                onChange={handleToggleComments}
              />
              Allow Comments
            </CheckboxLabel>
            <CheckboxLabel theme={theme}>
              <input
                type="checkbox"
                checked={blogDetails.notifications_enabled}
                onChange={() => handleToggleNotifications('notifications_enabled')}
              />
              Enable Email Notifications
            </CheckboxLabel>
            <CheckboxLabel theme={theme}>
              <input
                type="checkbox"
                checked={blogDetails.notify_new_post}
                onChange={() => handleToggleNotifications('notify_new_post')}
              />
              Notify on New Post
            </CheckboxLabel>
            <CheckboxLabel theme={theme}>
              <input
                type="checkbox"
                checked={blogDetails.notify_new_moment}
                onChange={() => handleToggleNotifications('notify_new_moment')}
              />
              Notify on New Moment
            </CheckboxLabel>
            <CheckboxLabel theme={theme}>
              <input
                type="checkbox"
                checked={blogDetails.notify_new_reaction}
                onChange={() => handleToggleNotifications('notify_new_reaction')}
              />
              Notify on New Reaction
            </CheckboxLabel>
            <CheckboxLabel theme={theme}>
              <input
                type="checkbox"
                checked={blogDetails.notify_new_comment}
                onChange={() => handleToggleNotifications('notify_new_comment')}
              />
              Notify on New Comment
            </CheckboxLabel>

            <div style={{ marginTop: 12, fontWeight: 700 }}>Metrics</div>

            <div style={{ marginTop: 8 }}>
              <RoleSelect
                theme={theme}
                value={blogDetails.metrics_mode || 'personal'}
                onChange={(e) => handleUpdateBlogField('metrics_mode', e.target.value)}
              >
                <option value="off">Off</option>
                <option value="personal">Personal</option>
                <option value="standard">Standard</option>
              </RoleSelect>
            </div>

            <CheckboxLabel theme={theme}>
              <input
                type="checkbox"
                checked={blogDetails.metrics_platform_opt_in}
                onChange={(e) =>
                  handleUpdateBlogField('metrics_platform_opt_in', e.target.checked)
                }
              />
              Opt in to Platform Metrics
            </CheckboxLabel>
          </Section>

          <Section theme={theme}>
            <SubHeading theme={theme}>Metrics (Blog Analytics)</SubHeading>
            <Button theme={theme} onClick={loadAnalytics} disabled={analyticsLoading}>
              {analyticsLoading ? 'Loading...' : 'Load Analytics'}
            </Button>

            {analytics && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Top Posts (30d)</div>
                {(analytics.top_posts || []).map((p) => (
                  <div key={p.post_id}>
                    <strong>{p.title}</strong> — {p.views_30d} views, {p.reactions_30d} reactions, {p.comments_30d} comments
                  </div>
                ))}

                <div style={{ fontWeight: 700, marginTop: 12, marginBottom: 6 }}>Top Moments (30d)</div>
                {(analytics.top_sections || []).map((s) => (
                  <div key={s.section_id}>
                    <strong>{s.title || `Moment ${s.order}`}</strong> — {s.views_30d} views
                  </div>
                ))}

                <div style={{ fontWeight: 700, marginTop: 12, marginBottom: 6 }}>Reaction Ratios</div>
                {(analytics.reaction_ratios || []).map((r) => (
                  <div key={r.reaction_type__id}>
                    {r.reaction_type__emoji || ''} {r.reaction_type__label}: {r.count}
                  </div>
                ))}

                <div style={{ fontWeight: 700, marginTop: 12, marginBottom: 6 }}>Comment Velocity</div>
                <div style={{ fontSize: '0.9rem', opacity: 0.85 }}>
                  {(analytics.comment_velocity || []).map((c) => `${c.date}:${c.count}`).join(' | ') || '—'}
                </div>
              </div>
            )}
          </Section>

          <Section theme={theme}>
            <SubHeading theme={theme}>Theme</SubHeading>
            <ThemeGrid>
              {themes.map((themeItem) => {

                const isSelected = String(themeItem.id) === String(selectedThemeId);
                return (
                  <ThemeCard
                    key={themeItem.id}
                    selected={isSelected}
                    theme={theme}
                    onClick={async () => {
                      
                      setSelectedThemeId(themeItem.id);

                      try {
                        await axios.patch(`/api/admin/blogs/${blog.id}/`, {
                          theme_id: themeItem.id,
                        });

                        const res = await axios.get(`/api/blogs/${blog.slug}/metadata/`);

                        const updatedBlog = { ...res.data }; // ✅ don’t keep role on the blog object anymore
                        selectBlog(updatedBlog);
                        await refreshBlogAuthz(); // ✅ perms update immediately


                        setBlogDetails((prev) => ({
                          ...prev,
                          theme_id: res.data.theme?.id,
                        }));

                        setSuccess(`Theme changed to ${themeItem.name}`);
                      } catch (err) {
                        console.error('Failed to save theme:', err);
                        setError('Theme update failed.');
                      }
                    }}

                  >
                    <ThemePreview $themeItem={themeItem} />
                    <ThemeColors>
                      <ColorSwatch color={themeItem.primary_color || '#ccc'} />
                      <ColorSwatch color={themeItem.secondary_color || '#eee'} />
                    </ThemeColors>
                    <div style={{ fontWeight: 600 }}>{themeItem.name}</div>
                    <FontSample $font={themeItem.font_family || 'sans-serif'}>
                      Aa Bb Cc
                    </FontSample>
                  </ThemeCard>
                );
              })}
            </ThemeGrid>
          </Section>



          <Section theme={theme}>
            <SubHeading theme={theme}>Members</SubHeading>
            <Table>
              <thead>
                <tr>
                  <Th theme={theme}>User</Th>
                  {ROLE_OPTIONS.map(({ label }) => (
                    <Th key={label} theme={theme}>{label}</Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {members.map(member => (
                  <tr key={member.user_id}>
                    <Td theme={theme}>{member.username}</Td>
                    {ROLE_OPTIONS.map(({ value }) => (
                      <Td key={value} theme={theme}>
                        <input
                          type="radio"
                          name={`role-${member.user_id}`}
                          checked={member.role === value}
                          onChange={() => handleRoleChange(member.user_id, value)}
                        />
                      </Td>
                    ))}
                  </tr>

                ))}
              </tbody>
            </Table>
          </Section>

          <Section theme={theme}>
            <InviteHeader theme={theme}>Invite Member</InviteHeader>

            <InviteContainer theme={theme}>
              <TabWrapper>
                <StyledTab
                  theme={theme}
                  active={activeTab === 'existing'}
                  onClick={() => setActiveTab('existing')}
                >
                  Existing User
                </StyledTab>
                <StyledTab
                  theme={theme}
                  active={activeTab === 'new'}
                  onClick={() => setActiveTab('new')}
                >
                  New User
                </StyledTab>
              </TabWrapper>

              <div style={{ marginTop: '2rem' }}>
                <InputRow>
                  {activeTab === 'existing' ? (
                    <select
                      multiple
                      value={selectedUserIds}
                      onChange={e =>
                        setSelectedUserIds(
                          Array.from(e.target.selectedOptions, option => option.value)
                        )
                      }
                      style={{
                        flex: 2,
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: `1px solid ${theme.inputBorder || '#ccc'}`,
                        height: '120px',
                      }}
                    >
                      {allUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.username}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      theme={theme}
                      type="email"
                      placeholder="Enter user's email"
                      value={usernameToInvite}
                      onChange={e => setUsernameToInvite(e.target.value)}
                    />
                  )}
                  <RoleSelect theme={theme} value={newUserRole} onChange={e => setNewUserRole(e.target.value)}>
                    {ROLE_OPTIONS.map(({ label, value }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </RoleSelect>
                  <Button theme={theme} onClick={handleInvite}>
                    {activeTab === 'new' ? 'Send Invite' : 'Invite'}
                  </Button>
                </InputRow>
              </div>
            </InviteContainer>
          </Section>


          {error && <ErrorText>{error}</ErrorText>}
          {success && <SuccessText>{success}</SuccessText>}
        </PanelContainer>
      </PageWrapper>
    </>
  );
};

export default BlogAdminPanel;
